import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import rawData from '../data/tarkovData.json';
import { slugify } from '../utils/slugs';
import { getCanonicalPath, getLanguageFromPath, stripLocalePrefix, withLocalePrefix } from '../utils/routes';

const DEFAULT_META = {
  title: 'Tarkov Stash | Escape from Tarkov Quest & Hideout Tracker',
  description: 'Tarkov Stash, also searched as Tarkovstash, helps you track Escape from Tarkov quests, hideout upgrades, Kappa items, trader loyalty, and story chapters.',
  lang: 'en',
};

const PAGE_META = {
  '/dashboard': {
    title: 'Tarkov Stash Dashboard | Escape from Tarkov Progress Tracker',
    description: 'Plan your next Escape from Tarkov tasks in Tarkov Stash, also searched as Tarkovstash, with quest progress, hideout stats, and Kappa progress.',
    lang: 'en',
  },
  '/quests': {
    title: 'Escape from Tarkov Quest Tracker | Tarkov Stash',
    description: 'Filter Escape from Tarkov quests by trader, level, completion state, prerequisites, and map.',
    lang: 'en',
  },
  '/hideout': {
    title: 'Tarkov Hideout Tracker | Tarkov Stash',
    description: 'Track Escape from Tarkov hideout modules, upgrade levels, and item requirements.',
    lang: 'en',
  },
  '/kappa-items': {
    title: 'Tarkov Kappa Items Tracker | Tarkov Stash',
    description: 'Track Collector items and Kappa container requirements for Escape from Tarkov.',
    lang: 'en',
  },
  '/kappa-path': {
    title: 'Tarkov Kappa Quest Path | Tarkov Stash',
    description: 'Visualize the Escape from Tarkov quest chain needed for Kappa progression.',
    lang: 'en',
  },
  '/story': {
    title: 'Tarkov Story Guide | Tarkov Stash',
    description: 'Follow Escape from Tarkov story chapters and quest guidance with multilingual support.',
    lang: 'en',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Tarkov Stash',
    description: 'Read the Tarkov Stash privacy policy, including cookies, Google AdSense, log files, and third-party services.',
    lang: 'en',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Tarkov Stash',
    description: 'Read the terms for using Tarkov Stash, an independent Escape from Tarkov progression tracker and guide site.',
    lang: 'en',
  },
  '/about': {
    title: 'About Us | Tarkov Stash',
    description: 'Learn about Tarkov Stash, an independent community tracker and guide hub for Escape from Tarkov players.',
    lang: 'en',
  },
  '/contact': {
    title: 'Contact | Tarkov Stash',
    description: 'Contact Tarkov Stash for feedback, privacy requests, support, or business inquiries.',
    lang: 'en',
  },
  '/tr/dashboard': {
    title: 'Tarkov Stash Panel | Escape from Tarkov Türkçe Takipçi',
    description: 'Tarkov Stash panelinde görevleri, hideout ilerlemesini, Kappa hedeflerini ve aktif görevleri Türkçe takip et.',
    lang: 'tr',
  },
  '/tr/quests': {
    title: 'Escape from Tarkov Türkçe Görev Rehberi | Tarkov Stash',
    description: 'Tarkov görevlerini trader, harita, seviye ve ön koşullara göre takip et. Türkçe EFT quest rehberi ve ilerleme takipçisi.',
    lang: 'tr',
  },
  '/tr/kappa-items': {
    title: 'Tarkov Kappa Eşyaları Türkçe | Tarkov Stash',
    description: 'Kappa Container için gereken Collector eşyalarını Türkçe takip et.',
    lang: 'tr',
  },
  '/tr/hideout': {
    title: 'Tarkov Hideout Rehberi Türkçe | Tarkov Stash',
    description: 'Escape from Tarkov hideout geliştirmelerini, modül seviyelerini ve gereken itemleri Türkçe takip et.',
    lang: 'tr',
  },
  '/tr/kappa-path': {
    title: 'Tarkov Kappa Rehberi ve Quest Sırası | Tarkov Stash',
    description: 'Kappa container için gereken görevleri, Collector itemlerini ve quest zincirini Türkçe takip et.',
    lang: 'tr',
  },
  '/tr/story': {
    title: 'Escape from Tarkov Hikaye Rehberi Türkçe | Tarkov Stash',
    description: 'Tarkov hikaye görevleri, bölümler ve ilerleme rehberi. Türkçe EFT story guide ve quest takibi.',
    lang: 'tr',
  },
  '/tr/privacy-policy': {
    title: 'Gizlilik Politikası | Tarkov Stash',
    description: 'Tarkov Stash gizlilik politikası: çerezler, Google AdSense, log dosyaları ve üçüncü taraf hizmetler.',
    lang: 'tr',
  },
  '/tr/terms-of-service': {
    title: 'Kullanım Şartları | Tarkov Stash',
    description: 'Tarkov Stash kullanım şartlarını okuyun.',
    lang: 'tr',
  },
  '/tr/about': {
    title: 'Hakkımızda | Tarkov Stash',
    description: 'Tarkov Stash hakkında bilgi alın: Escape from Tarkov oyuncuları için bağımsız takipçi ve rehber merkezi.',
    lang: 'tr',
  },
  '/tr/contact': {
    title: 'İletişim | Tarkov Stash',
    description: 'Geri bildirim, destek, gizlilik talepleri veya iş birlikleri için Tarkov Stash ile iletişime geçin.',
    lang: 'tr',
  },
  '/ru/dashboard': {
    title: 'Tarkov Stash Dashboard | Escape from Tarkov Progress Tracker',
    description: 'Track Escape from Tarkov quests, hideout progress, Kappa items, and story chapters with Russian language support.',
    lang: 'ru',
  },
  '/ru/quests': {
    title: 'Escape from Tarkov Quest Tracker на русском | Tarkov Stash',
    description: 'Отслеживайте квесты Escape from Tarkov по торговцу, карте, уровню и прогрессу.',
    lang: 'ru',
  },
  '/ru/kappa-items': {
    title: 'Tarkov Kappa Items Tracker | Tarkov Stash',
    description: 'Track Collector items and Kappa container requirements with Russian language support.',
    lang: 'ru',
  },
  '/ru/hideout': {
    title: 'Tarkov Hideout Tracker | Tarkov Stash',
    description: 'Track Escape from Tarkov hideout modules and requirements with Russian language support.',
    lang: 'ru',
  },
  '/ru/kappa-path': {
    title: 'Tarkov Kappa Quest Path | Tarkov Stash',
    description: 'Visualize the Kappa quest chain with Russian language support.',
    lang: 'ru',
  },
  '/ru/story': {
    title: 'Escape from Tarkov Story Guide | Tarkov Stash',
    description: 'Follow Escape from Tarkov story chapters with Russian language support.',
    lang: 'ru',
  },
  '/ru/privacy-policy': {
    title: 'Privacy Policy | Tarkov Stash',
    description: 'Read the Tarkov Stash privacy policy, including cookies, Google AdSense, log files, and third-party services.',
    lang: 'ru',
  },
  '/ru/terms-of-service': {
    title: 'Terms of Service | Tarkov Stash',
    description: 'Read the terms for using Tarkov Stash, an independent Escape from Tarkov progression tracker and guide site.',
    lang: 'ru',
  },
  '/ru/about': {
    title: 'About Us | Tarkov Stash',
    description: 'Learn about Tarkov Stash, an independent community tracker and guide hub for Escape from Tarkov players.',
    lang: 'ru',
  },
  '/ru/contact': {
    title: 'Contact | Tarkov Stash',
    description: 'Contact Tarkov Stash for feedback, privacy requests, support, or business inquiries.',
    lang: 'ru',
  },
};

const STORY_META = {
  batya: {
    title: 'Batya Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash',
    trTitle: 'Batya Rehberi (TR/EN/RU) | Escape from Tarkov Hikaye | Tarkov Stash',
    ruTitle: 'Batya Гайд (EN/TR/RU) | Escape from Tarkov | Tarkov Stash',
    description: 'Batya chapter guide for Escape from Tarkov. Learn the trigger locations, objectives, item steps, and story progression.',
    trDescription: 'Escape from Tarkov Batya bölümü rehberi. Başlatma konumlarını, hedefleri, item adımlarını ve hikaye ilerlemesini takip et.',
    ruDescription: 'Гайд по главе Batya в Escape from Tarkov: точки запуска, цели, предметы и сюжетное прохождение.',
  },
  unheard: { title: 'Unheard Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  'the-ticket': { title: 'The Ticket Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  tour: { title: 'Tour Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  'accidental-witness': { title: 'Accidental Witness Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  'falling-skies': { title: 'Falling Skies Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  labyrinth: { title: 'Labyrinth Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  'they-are-already-here': { title: 'They Are Already Here Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
  'blue-flame': { title: 'Blue Flame Guide (EN/TR/RU) | Escape from Tarkov Story | Tarkov Stash' },
};

const trimDescription = (value, maxLength = 155) => {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength - 1).replace(/\s+\S*$/, '')}…`;
};

const findQuestBySlug = (questSlug) => {
  const quests = Object.values(rawData.tasks || {});
  return quests.find((quest) => {
    const names = [
      quest.name,
      quest.locales?.en?.name,
      quest.locales?.tr?.name,
      quest.locales?.ru?.name,
    ];

    return names.filter(Boolean).some((name) => {
      const baseSlug = slugify(name);
      return baseSlug === questSlug || `${baseSlug}-${quest.id}` === questSlug;
    });
  });
};

const getQuestMeta = (pathname) => {
  const canonicalPath = getCanonicalPath(pathname);
  const corePath = stripLocalePrefix(canonicalPath);
  const match = corePath.match(/^\/quest\/([^/]+)$/);
  if (!match) return null;

  const lang = getLanguageFromPath(canonicalPath) || 'en';
  const quest = findQuestBySlug(match[1]);
  if (!quest) return null;

  const locale = quest.locales?.[lang] || quest.locales?.en || {};
  const enLocale = quest.locales?.en || {};
  const titleName = locale.name || enLocale.name || quest.name;
  const primaryObjective = (locale.objectives || enLocale.objectives || quest.objectives || [])
    .map((objective) => objective.description)
    .filter(Boolean)[0] || '';

  const guideLabel = lang === 'tr' ? 'Görev Rehberi' : lang === 'ru' ? 'Гайд по квесту' : 'Quest Guide';
  const title = `${titleName} ${guideLabel} (EN/TR/RU) | Escape from Tarkov | Tarkov Stash`;
  const description = trimDescription(
    lang === 'tr'
      ? `${titleName} görevi için Türkçe, İngilizce ve Rusça Escape from Tarkov rehberi. ${primaryObjective}`
      : lang === 'ru'
        ? `${titleName}: гайд Escape from Tarkov на EN/TR/RU. ${primaryObjective}`
        : `${titleName} quest guide for Escape from Tarkov with EN/TR/RU support. ${primaryObjective}`
  );

  return { title, description, lang };
};

const getStoryMeta = (pathname) => {
  const canonicalPath = getCanonicalPath(pathname);
  const corePath = stripLocalePrefix(canonicalPath);
  const match = corePath.match(/^\/story\/([^/]+)$/);
  if (!match) return null;

  const lang = getLanguageFromPath(canonicalPath) || 'en';
  const meta = STORY_META[match[1]];
  if (!meta) return null;

  const title = lang === 'tr' ? meta.trTitle || meta.title : lang === 'ru' ? meta.ruTitle || meta.title : meta.title;
  const description = lang === 'tr'
    ? meta.trDescription || 'Escape from Tarkov hikaye rehberi, görev adımları ve ilerleme notları.'
    : lang === 'ru'
      ? meta.ruDescription || 'Сюжетный гайд Escape from Tarkov с этапами и целями.'
      : meta.description || 'Escape from Tarkov story chapter guide with EN/TR/RU support.';

  return { title, description, lang };
};

const getRouteMeta = (pathname) => {
  const canonicalPath = getCanonicalPath(pathname);
  const questMeta = getQuestMeta(pathname);
  if (questMeta) return questMeta;
  const storyMeta = getStoryMeta(pathname);
  if (storyMeta) return storyMeta;
  if (canonicalPath.startsWith('/story/')) return PAGE_META['/story'];
  return PAGE_META[canonicalPath] || PAGE_META[stripLocalePrefix(canonicalPath)] || DEFAULT_META;
};

const upsertMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const upsertAlternate = (hrefLang, href) => {
  let element = document.head.querySelector(`link[rel="alternate"][hreflang="${hrefLang}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', hrefLang);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

function Seo() {
  const location = useLocation();

  useEffect(() => {
    const meta = getRouteMeta(location.pathname);
    const canonicalPath = getCanonicalPath(location.pathname);
    const canonicalUrl = `https://tarkovstash.com${canonicalPath === '/' ? '/' : canonicalPath}`;
    const corePath = stripLocalePrefix(canonicalPath);

    document.title = meta.title;
    document.documentElement.lang = meta.lang || 'en';
    upsertMeta('name', 'description', meta.description);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:locale', meta.lang === 'tr' ? 'tr_TR' : meta.lang === 'ru' ? 'ru_RU' : 'en_US');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertCanonical(canonicalUrl);
    upsertAlternate('tr', `https://tarkovstash.com${withLocalePrefix(corePath, 'tr')}`);
    upsertAlternate('ru', `https://tarkovstash.com${withLocalePrefix(corePath, 'ru')}`);
    upsertAlternate('en', `https://tarkovstash.com${withLocalePrefix(corePath, 'en')}`);
    upsertAlternate('x-default', 'https://tarkovstash.com/');
  }, [location.pathname]);

  return null;
}

export default Seo;
